import api from "../axios";

export type PolicyDocumentType =
  | "rental-letter-of-credit"
  | "automatic-billing-authorization"
  | "acknowledgement";

/**
 * Lifecycle:
 *   pending  → ACH purchase awaiting settlement (1–3 day window)
 *   covered  → active coverage
 *   revoked  → terminated (manually or by failed ACH after `pending`)
 *   expired  → past `endsOn` without renewal
 */
export type PolicyStatus = "pending" | "covered" | "revoked" | "expired";

export type PolicyTermStatus =
  | "future"
  | "processing"
  | "scheduled"
  | "cancelled"
  | "confirmed"
  | "active"
  | "expired"
  | "void";

export interface PolicyTermSummary {
  uuid: string;
  coverageAmount: number;
  monthlyRentAmount: number;
  startsOn: string | null;
  endsOn: string | null;
  status: PolicyTermStatus;
  renewalTerm?: "fixed" | "month-to-month";
  /** Quote total in dollars. Null when no quote is attached yet. */
  total: number | null;
  cancelledAt?: string;
  confirmedAt?: string;
  /** Admin waived the late fee — suppresses it on this term's payment. */
  lateFeeWaived?: boolean;
  /** Set once a late fee has actually been charged; never charged twice. */
  lateFeeAppliedAt?: string | null;
  /** Admin-granted credit against this term's charge, in dollars. */
  creditAmount?: number | null;
  /** Set once the credit has been spent; it does not apply a second time. */
  creditAppliedAt?: string | null;
}

export interface PolicySummary {
  id: number;
  uuid: string;
  createdAt: string;
  name: string;
  status: PolicyStatus;
  coverageAmount: number;
  startsOn: string | null;
  endsOn: string | null;
  renewalTerm: "fixed" | "month-to-month";
  unit: {
    id: number;
    uuid: string;
    name: string;
    property: {
      id: number;
      uuid: string;
      name: string;
      address: string;
    } | null;
  } | null;
  account: {
    id: number;
    uuid: string;
    companyName: string;
  } | null;
  currentTerm: PolicyTermSummary | null;
  nextTerm: PolicyTermSummary | null;
}

export function getMyPolicies() {
  return api.get<PolicySummary[]>("/policies");
}

export function getPolicy(uuid: string) {
  return api.get<PolicySummary>(`/policies/${uuid}`);
}

export function getPolicyDocumentDownloadUrl(
  uuid: string,
  documentType: PolicyDocumentType,
) {
  return api.get<{ downloadUrl: string }>(
    `/policies/${uuid}/download/${documentType}`,
  );
}

export type PayRenewalStatus =
  | "pending"
  | "processing"
  | "succeeded"
  | "failed"
  | "cancelled";

export interface PayRenewalResult {
  transactionUuid: string;
  status: PayRenewalStatus;
  amount: number;
  paymentIntentId: string;
  promotedTermUuid: string | null;
  error?: string;
}

export function payRenewal(
  uuid: string,
  paymentMethodId: number,
  /**
   * Required for bank-account PMs; ignored for cards. Server reads
   * IP + User-Agent from the request itself (not trusted from client).
   */
  mandateAccepted?: boolean,
) {
  return api.post<PayRenewalResult>(`/policies/${uuid}/pay-renewal`, {
    paymentMethodId,
    ...(mandateAccepted ? { mandateAccepted: true } : {}),
  });
}

// ---- Change requests to the active term --------------------------------
// The property manager (via Lighthouse) asked to change coverage or dates;
// when the new terms cost more, the tenant pays the difference here.

export type TermChangeRequestStatus =
  | "pending_payment"
  | "processing"
  | "applied"
  | "cancelled";

export interface TermChangeRequest {
  uuid: string;
  status: TermChangeRequestStatus;
  createdAt: string;
  policyUuid: string;
  policyName: string;
  termUuid: string;
  previous: { coverageAmount: number; startsOn: string; endsOn: string | null };
  requested: { coverageAmount: number; startsOn: string; endsOn: string };
  /** Dollars. */
  previousAmount: number;
  newAmount: number;
  /** Dollars the tenant owes (positive) for a pending request. */
  difference: number;
}

export interface PayTermChangeResult {
  status: PayRenewalStatus;
  requestStatus: TermChangeRequestStatus;
  /** Dollars. */
  amount: number;
  error?: string;
}

/** Requests awaiting the tenant's payment, or with a payment settling. */
export function getOpenChangeRequests() {
  return api.get<TermChangeRequest[]>("/policies/change-requests");
}

export function getChangeRequest(requestUuid: string) {
  return api.get<TermChangeRequest>(`/policies/change-requests/${requestUuid}`);
}

export function payChangeRequest(
  requestUuid: string,
  paymentMethodId: number,
  /** Required for bank-account PMs; ignored for cards. */
  mandateAccepted?: boolean,
) {
  return api.post<PayTermChangeResult>(
    `/policies/change-requests/${requestUuid}/pay`,
    {
      paymentMethodId,
      ...(mandateAccepted ? { mandateAccepted: true } : {}),
    },
  );
}
